import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Section, Container, SectionHead } from './Section';
import { projects } from '../data/portfolio';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled(motion.article)`
  position: relative;
  border-radius: ${({ theme }) => theme.radii.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: transform 300ms ease, border-color 300ms ease;

  &:hover {
    transform: translateY(-6px);
    border-color: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgSoft};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 800ms ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 40%, rgba(11,11,18,0.9) 100%);
  }

  ${Card}:hover & img { transform: scale(1.08); }
`;

const Tags = styled.div`
  position: absolute;
  z-index: 2;
  top: 14px;
  left: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  span {
    font-size: 11px;
    padding: 4px 10px;
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(21,21,31,0.75)' : 'rgba(255,255,255,0.85)'};
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 999px;
    color: ${({ theme }) => theme.colors.text};
    backdrop-filter: blur(6px);
  }
`;

const Meta = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 14px;
  left: 18px;
  right: 18px;
  display: flex;
  align-items: end;
  justify-content: space-between;

  .sub {
    font-family: ${({ theme }) => theme.fonts.mono};
    color: ${({ theme }) => theme.colors.primarySoft};
    font-size: 12px;
    margin-bottom: 4px;
  }
  h3 { font-size: 22px; color: white; }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: transform 200ms ease;
    flex-shrink: 0;
  }

  ${Card}:hover & .icon { transform: rotate(45deg); }
`;

const Body = styled.div`
  padding: 20px 22px 24px;

  p {
    color: ${({ theme }) => theme.colors.textDim};
    font-size: 14px;
    line-height: 1.7;
    margin-bottom: 14px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12px;
    font-family: ${({ theme }) => theme.fonts.mono};
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
`;

export default function Portfolio() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Section id="portfolio" ref={ref}>
      <Container>
        <SectionHead kicker="Portfolio" title="Selected" gradient="Projects" sub="A handful of products I helped design, build, and ship." />
        <Grid>
          {projects.map((p, i) => (
            <Card key={p.title} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.1, duration: 0.6 }}>
              <Media>
                <Tags>{p.tags.map((t) => <span key={t}>{t}</span>)}</Tags>
                <img src={p.image} alt={p.title} loading="lazy" />
                <Meta>
                  <div>
                    <div className="sub">{p.subtitle}</div>
                    <h3>{p.title}</h3>
                  </div>
                </Meta>
              </Media>
              <Body>
                <p>{p.description}</p>
                <div className="row"><span>{p.role}</span><span>{p.year}</span></div>
              </Body>
            </Card>
          ))}
        </Grid>
      </Container>
    </Section>
  );
}
